import type { ReactNode } from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import type { Budget, BudgetOwner, Transaction } from "../../../types";
import type { TransactionFilters } from "../../../app/views";
import { money, plural } from "../../../lib/finance";
import { categorySpend } from "../../../lib/budgets";
import Avatar from "../../../components/Avatar";
import { Private, usePrivacy } from "../../../components/Privacy";

export default function MonthlySpending({
  transactions, expenses, spending, income, month, monthName,
  budgetOwner, overallBudget, categoryBudgetRows, collapsed, collapseButton, showTransactions,
}: {
  transactions: Transaction[];
  expenses: Transaction[];
  spending: number | null;
  income: number | null;
  month: string;
  monthName: string;
  budgetOwner: BudgetOwner;
  overallBudget: Budget | undefined;
  categoryBudgetRows: { category: string; budget: Budget }[];
  collapsed: boolean;
  /** The module's collapse toggle, drawn by the caller so every module's matches. */
  collapseButton: ReactNode;
  showTransactions: (filters: TransactionFilters) => void;
}) {
  return (
    <section className={`spending-card overview-module${collapsed ? " is-collapsed" : ""}`}>
      <div className="card-topline">
        <span><span className="status-dot" /> MONTHLY SPENDING</span>
        <div className="overview-heading-actions">
          <strong className="collapsed-summary"><Private>{money(spending)}</Private></strong>
          <button className="overview-module-link" aria-label="View expense transactions" onClick={() => showTransactions({ type: "Expense" })}><ArrowUpRight size={19} /></button>
          {collapseButton}
        </div>
      </div>
      <div className="spending-number">
        {spending === null
          ? "Unavailable"
          : <Private>{money(spending, "MVR").replace(" MVR", "")}</Private>}
        <span>MVR</span>
      </div>
      <div className="spending-caption">
        {plural(expenses.length, "expense")} <span>·</span> {monthName}
      </div>
      {overallBudget && overallBudget.amount !== null && spending !== null && (
        <BudgetLine owner={budgetOwner} spent={spending} amount={overallBudget.amount} currency={overallBudget.currency} />
      )}
      {categoryBudgetRows.map(({ category, budget }) => (
        <BudgetLine
          key={category}
          heading={category}
          owner={budgetOwner}
          spent={categorySpend(transactions, budgetOwner, category, month)}
          amount={budget.amount!}
          currency={budget.currency}
        />
      ))}
      <div className="spending-bottom">
        <span>
          <ArrowDownLeft size={16} /> Income this month
        </span>
        <strong><Private>{money(income)}</Private></strong>
      </div>
    </section>
  );
}

/** How much of one budget is spent: the household-wide one, or one category's under a heading. */
function BudgetLine({ heading, owner, spent, amount, currency }: {
  heading?: string;
  owner: BudgetOwner;
  spent: number;
  amount: number;
  currency: string;
}) {
  // The bar shows how much is spent, so it goes along with the figures when amounts are hidden.
  const { hidden } = usePrivacy();
  return (
    <div className="spending-budget">
      {heading && <h4>{heading}</h4>}
      {!hidden && (
        <div className={"budget-bar" + (spent > amount ? " over" : "")}>
          <div className="budget-bar-track">
            <div
              className="budget-bar-fill"
              style={{ width: `${amount > 0 ? Math.min(100, (spent / amount) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}
      <span className="spending-budget-caption">
        <Avatar owner={owner} size={16} />
        <Private>{money(spent, currency)}</Private> of{" "}
        {money(amount, currency)}
      </span>
    </div>
  );
}
